import Step from '../../step/step'
import { PaymentMethodSelector } from '../../payment-methods-step/components/payment-method-selector'
import { PaymentMethodActionButton } from '../../payment-methods-step/components/payment-method-action-button'
import DonationSummary from './components/donation-summary/donation-summary'
import { Section } from '../section/section'
import useConfig from 'modules/config/state/use-config'
import { useTranslation } from 'next-i18next'

import DonorInfo from './components/donor-info/donor-info'
import ContactQuestionsForm from './components/contact-questions-form/contact-questions-form'
import CustomQuestions from './components/custom-questions/custom-questions'
import EmployerMatching from './components/employer-matching/employer-matching'
import PaymentSecureHeader from '../../payment-methods-step/components/payment-secure-header'
import DesignationPreConversion from 'modules/order/components/designation/designation-pre-conversion'
import OrgDonationOption from './components/donation-options/org-donation-option'

import useOrder from '../../../../order/state/order/use-order'
import { useVisibleCustomQuestions } from 'hooks/use-visible-questions'
import DedicationPreConversionPreview from 'modules/order/components/dedication/dedication-pre-conversion-preview'
import { useSelector } from 'react-redux'
import { RootState } from 'redux/store'
import LegalText from './components/legal-text/legal-text'
import { FloatingOptions } from './components/donation-options/floating-options'

export default function SingleStep() {
  const { t } = useTranslation()
  const config = useConfig()
  const order = useOrder()
  const urlParams = useSelector((state: RootState) => state.urlParams)

  const hasDedicationPreview = !!urlParams.dedication

  const isBankTransferSelected = order.paymentDetails.type === 'ACH'
  const isCreditCardSelected = order.paymentDetails.type === 'CC'

  // Check if there are any required contact questions
  const hasRequiredContactQuestions = config.questions.contact.some(
    (question) => question.is_required,
  )

  // Get visible custom questions using the hook
  const visibleRequiredCustomQuestions = useVisibleCustomQuestions(config.questions.custom, true)

  // Check if there are any visible custom questions for required fields
  const hasRequiredCustomQuestions = visibleRequiredCustomQuestions.length > 0

  // Feature visibility
  const shouldShowDesignation = config.designation.isEnabled

  const shouldShowEmployerMatching =
    config.doubleTheDonation.isEnabled || config.organization.has_employer_match

  // Section visibility
  // Show DonorInfo for Credit Card/Plaid when billing address is required (new logic)
  const shouldShowDonorInfo =
    isBankTransferSelected || (isCreditCardSelected && config.isBillingAddressRequired)

  const shouldShowFloatingOptions =
    config.anonymous.isHideNameEnabled ||
    config.anonymous.isHideAmountEnabled ||
    config.optIn.isEnabled ||
    config.optInSms.isEnabled

  // Show Donor Information section if shouldShowDonorInfo is true AND there's at least one question
  const shouldShowDonorInformationSection =
    shouldShowDonorInfo || hasRequiredContactQuestions || hasRequiredCustomQuestions

  const shouldShowDonationOptions =
    shouldShowDesignation ||
    shouldShowEmployerMatching ||
    config.payment.corporateDonationEnabled ||
    (!shouldShowDonorInformationSection && shouldShowFloatingOptions)

  return (
    <Step.Root>
      <DedicationPreConversionPreview />
      <Step.Content hasDedicationPreview={hasDedicationPreview}>
        <PaymentMethodSelector />

        {shouldShowDonationOptions && (
          <Section gap={16} title={t('singleStep.donationOptions.title', 'Donation options')}>
            {shouldShowDesignation && <DesignationPreConversion />}
            {shouldShowEmployerMatching && <EmployerMatching />}
            {config.payment.corporateDonationEnabled && <OrgDonationOption />}

            {!shouldShowDonorInformationSection && shouldShowFloatingOptions && <FloatingOptions />}
          </Section>
        )}

        {shouldShowDonorInformationSection && (
          <Section
            title={t('singleStep.donorInformation.title', 'Your information')}
            gap={16}
            titleGap={16}
          >
            {shouldShowDonorInfo && <DonorInfo />}
            {hasRequiredContactQuestions && <ContactQuestionsForm showRequired />}
            {hasRequiredCustomQuestions && <CustomQuestions showRequired />}
            {shouldShowFloatingOptions && <FloatingOptions />}
          </Section>
        )}

        <Section title={t('singleStep.donationSummary.title', 'Donation summary')} gap={16}>
          <DonationSummary />
        </Section>

        <PaymentMethodActionButton dataIdPrefix="single-step-payment-button" />
        <PaymentSecureHeader />
        <LegalText />
      </Step.Content>
    </Step.Root>
  )
}
